import { useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import Input from '@/components/shared/Input';
import { OpenStreetMapProvider, SearchResult } from 'leaflet-geosearch';
import UIMapDynamic from '@/components/shared/UIMapDynamic';
import './AddPropertyForm.scss';
import Button from '@/components/shared/Button';
import { createPropertyRequest } from '@/api';
import { IProperty } from '@/types';
import { useNavigate } from 'react-router-dom';
import convertAddressToStringWithoutZip from '@/utils/convertAddressToStringWithoutZip';
import * as Yup from 'yup';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import { useToast } from '@/components/shared/ToastProvider';
import axios, { AxiosError } from 'axios';

const provider = new OpenStreetMapProvider();

const baseClass = 'AddPropertyForm';

interface AddPropertyFormProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
}

interface FormikValues {
  name: string;
  country: string;
  stateOrProvince: string | null;
  city: string | null;
  postalCode: string | null;
  buildingNumber: string | null;
  street: string | null;
  buildingType: 'detachedHouse' | 'apartmentBuilding'; // isDetachedHouse: boolean;
  latitude: number | null;
  longitude: number | null;
}

const initialValues: FormikValues = {
  name: '',
  country: 'Canada',
  stateOrProvince: null,
  city: null,
  postalCode: null,
  buildingNumber: null,
  street: null,
  buildingType: 'apartmentBuilding',
  latitude: null,
  longitude: null,
};

const validationSchema = Yup.object({
  name: Yup.string().min(2, 'Too short').required('Required property name'),
  country: Yup.string().required('Required Country'),
  stateOrProvince: Yup.string().nullable(),
  city: Yup.string().nullable(),
  postalCode: Yup.string().nullable(),
  buildingNumber: Yup.string().nullable(),
  street: Yup.string().nullable(),
  buildingType: Yup.string().oneOf(['detachedHouse', 'apartmentBuilding']),
  latitude: Yup.number()
    .nullable()
    .min(-90, 'Invalid latitude')
    .max(90, 'Invalid latitude'),
  longitude: Yup.number()
    .nullable()
    .min(-180, 'Invalid longitude')
    .max(180, 'Invalid longitude'),
});

const AddPropertyForm: React.FC<AddPropertyFormProps> = ({
  collapsed,
  onToggleCollapse,
  className = '',
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [timer, setTimer] = React.useState<NodeJS.Timer | null>(null);

  const [countryState, setCountryState] = React.useState<string>('');
  const [stateState, setStateState] = React.useState<string>('');
  const [cityState, setCityState] = React.useState<string>('');
  const [postalCodeState, setPostalCodeState] = React.useState<string>('');
  const [buildingNumberState, setBuildingNumberState] =
    React.useState<string>('');
  const [streetState, setStreetState] = React.useState<string>('');
  const [latitudeState, setLatitudeState] = React.useState<number | null>(null);
  const [longitudeState, setLongitudeState] = React.useState<number | null>(
    null,
  );
  const [rawLatitude, setRawLatitude] = React.useState('');
  const [rawLongitude, setRawLongitude] = React.useState('');
  const [isMapSearch, setIsMapSearch] = React.useState<boolean>(false);
  const [submissionError, setSubmissionError] = React.useState<string | null>(
    null,
  );

  const [searchResults, setSearchResults] = React.useState<SearchResult[]>([]);

  const { mutate } = useMutation({
    mutationFn: createPropertyRequest,
  });

  useEffect(() => {
    if (timer) {
      clearTimeout(timer);
    }

    // Priority 1: Valid coordinates are entered.
    if (latitudeState != null && longitudeState != null) {
      setSearchResults([
        {
          x: longitudeState,
          y: latitudeState,
          label: 'Manual coordinates',
          raw: {
            type: 'building', // Pass type for UIMapDynamic filter
            lat: latitudeState.toString(),
            lon: longitudeState.toString(),
            display_name: 'Manual coordinates',
          },
        } as unknown as SearchResult,
      ]);
      return;
    }

    // Priority 2: User is typing in coordinate fields. Block address search.
    if (rawLatitude.length > 0 || rawLongitude.length > 0) {
      setSearchResults([]); // Clear map while typing incomplete/invalid coords
      return;
    }

    // Priority 3: Fallback to address search.
    setIsMapSearch(true);
    const query = convertAddressToStringWithoutZip({
      buildingNumber: buildingNumberState,
      street: streetState,
      city: cityState,
      stateOrProvince: stateState,
      country: countryState,
    });

    if (query) {
      setTimer(
        setTimeout(() => {
          provider
            .search({ query: query.trim() || '' })
            .then(setSearchResults)
            .catch(console.error)
            .finally(() => {
              setIsMapSearch(false);
            });
        }, 1000),
      );
    } else {
      setSearchResults([]);
      setIsMapSearch(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line
  }, [
    countryState,
    stateState,
    cityState,
    postalCodeState,
    buildingNumberState,
    streetState,
    latitudeState,
    longitudeState,
    rawLatitude,
    rawLongitude,
  ]);

  const renderContent = () => (
    <div className={`${baseClass}__content`}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm, setErrors, setSubmitting }) => {
          setSubmissionError(null);
          const data: IProperty = {
            name: values.name,
            address: {
              country: values.country,
              stateOrProvince: values.stateOrProvince,
              city: values.city,
              postalCode: values.postalCode,
              buildingNumber: values.buildingNumber,
              street: values.street,
              latitude: values.latitude ?? searchResults[0]?.y ?? null,
              longitude: values.longitude ?? searchResults[0]?.x ?? null,
            },
          };
          setSubmitting(true);
          mutate(data, {
            onSuccess: () => {
              showSuccess('Property created successfully!');
              navigate('/dashboard/properties');
            },
            onError: (error: Error) => {
              if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<{
                  message: string;
                  errors: Record<string, string>;
                }>;
                if (axiosError.response?.data?.errors) {
                  setErrors(axiosError.response.data.errors);
                  showError('Please correct the errors and try again.');
                } else if (axiosError.response?.data?.message) {
                  setSubmissionError(axiosError.response.data.message);
                  showError(axiosError.response.data.message);
                } else {
                  const message =
                    'An unexpected error occurred. Please try again.';
                  setSubmissionError(message);
                  showError(axiosError.message || message);
                }
              } else {
                const message =
                  'An unexpected error occurred. Please try again.';
                setSubmissionError(error.message || message);
                showError(error.message || message);
              }
            },
            onSettled: () => {
              setSubmitting(false);
            },
          });
        }}
        onReset={() => {
          setSearchResults([]);
          setCountryState('');
          setStateState('');
          setCityState('');
          setPostalCodeState('');
          setBuildingNumberState('');
          setStreetState('');
          setRawLatitude('');
          setRawLongitude('');
          setLatitudeState(null);
          setLongitudeState(null);
        }}
      >
        {({
          values,
          errors,
          handleBlur,
          touched,
          setFieldValue,
          isSubmitting,
          dirty,
        }: FormikProps<FormikValues>) => {
          const handleFieldChange =
            (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
              setSubmissionError(null);
              const { value } = event.target;

              // Special handling for coordinate fields
              if (field === 'latitude' || field === 'longitude') {
                // Pasted coordinates
                if (value.includes(',')) {
                  const [lat, lon] = value.split(',').map((s) => s.trim());
                  setRawLatitude(lat);
                  setRawLongitude(lon);

                  const parsedLat = parseFloat(lat);
                  const parsedLon = parseFloat(lon);
                  setFieldValue(
                    'latitude',
                    isNaN(parsedLat) ? null : parsedLat,
                  );
                  setFieldValue(
                    'longitude',
                    isNaN(parsedLon) ? null : parsedLon,
                  );
                  setLatitudeState(isNaN(parsedLat) ? null : parsedLat);
                  setLongitudeState(isNaN(parsedLon) ? null : parsedLon);
                } else {
                  // Regular typing
                  if (field === 'latitude') {
                    setRawLatitude(value);
                    const parsedLat = parseFloat(value);
                    setFieldValue(
                      'latitude',
                      isNaN(parsedLat) ? null : parsedLat,
                    );
                    setLatitudeState(isNaN(parsedLat) ? null : parsedLat);
                  } else {
                    // longitude
                    setRawLongitude(value);
                    const parsedLon = parseFloat(value);
                    setFieldValue(
                      'longitude',
                      isNaN(parsedLon) ? null : parsedLon,
                    );
                    setLongitudeState(isNaN(parsedLon) ? null : parsedLon);
                  }
                }
                return;
              }

              // Handle other fields
              setFieldValue(field, value);
              switch (field) {
                case 'country':
                  setCountryState(value);
                  break;
                case 'stateOrProvince':
                  setStateState(value);
                  break;
                case 'city':
                  setCityState(value);
                  break;
                case 'postalCode':
                  setPostalCodeState(value);
                  break;
                case 'buildingNumber':
                  setBuildingNumberState(value);
                  break;
                case 'street':
                  setStreetState(value);
                  break;
              }
            };

          return (
            <>
              <Form className={`${baseClass}-form`}>
                <div className={`${baseClass}-form__body`}>
                  <div className={`${baseClass}-form-group`}>
                    <Input
                      className={`${baseClass}-form-group-item`}
                      label={'Name*'}
                      floatLabel={true}
                      name={'name'}
                      onChange={handleFieldChange('name')}
                      onBlur={handleBlur}
                      value={values.name}
                      errors={errors.name && touched.name ? errors.name : ''}
                    />
                  </div>
                  <div className={`${baseClass}-form-group`}>
                    <Input
                      className={`${baseClass}-form-group-item`}
                      label={'Street'}
                      floatLabel
                      name={'street'}
                      onChange={handleFieldChange('street')}
                      onBlur={handleBlur}
                      value={values.street}
                      errors={
                        errors.street && touched.street ? errors.street : ''
                      }
                    />
                    <Input
                      className={`${baseClass}-form-group-item short-field`}
                      label={'Building number'}
                      floatLabel
                      name={'buildingNumber'}
                      onChange={handleFieldChange('buildingNumber')}
                      onBlur={handleBlur}
                      value={values.buildingNumber}
                      errors={
                        errors.buildingNumber && touched.buildingNumber
                          ? errors.buildingNumber
                          : ''
                      }
                    />
                  </div>
                  <div className={`${baseClass}-form-group`}>
                    <Input
                      className={`${baseClass}-form-group-item`}
                      floatLabel
                      label={'City'}
                      name={'city'}
                      onChange={handleFieldChange('city')}
                      onBlur={handleBlur}
                      value={values.city}
                      errors={errors.city && touched.city ? errors.city : ''}
                    />
                    <Input
                      className={`${baseClass}-form-group-item`}
                      label={
                        values.country.trim().toUpperCase() !== 'CANADA'
                          ? 'State'
                          : 'Province'
                      }
                      floatLabel
                      name={'stateOrProvince'}
                      onChange={handleFieldChange('stateOrProvince')}
                      onBlur={handleBlur}
                      value={values.stateOrProvince}
                      errors={
                        errors.stateOrProvince && touched.stateOrProvince
                          ? errors.stateOrProvince
                          : ''
                      }
                    />
                    <Input
                      className={`${baseClass}-form-group-item short-field`}
                      label={'Postal code'}
                      floatLabel={true}
                      name={'postalCode'}
                      onChange={handleFieldChange('postalCode')}
                      onBlur={handleBlur}
                      value={values.postalCode}
                      errors={
                        errors.postalCode && touched.postalCode
                          ? errors.postalCode
                          : ''
                      }
                    />
                  </div>
                  <div className={`${baseClass}-form-group`}>
                    <Input
                      className={`${baseClass}-form-group-item`}
                      label={'Country*'}
                      floatLabel={true}
                      name={'country'}
                      onChange={handleFieldChange('country')}
                      onBlur={handleBlur}
                      value={values.country}
                      errors={
                        errors.country && touched.country ? errors.country : ''
                      }
                    />
                  </div>
                  <div className={`${baseClass}-form-group`}>
                    <Input
                      className={`${baseClass}-form-group-item`}
                      label={'Latitude'}
                      floatLabel
                      name={'latitude'}
                      type="text"
                      onChange={handleFieldChange('latitude')}
                      onBlur={handleBlur}
                      value={rawLatitude}
                      errors={
                        errors.latitude && touched.latitude
                          ? errors.latitude
                          : ''
                      }
                    />
                    <Input
                      className={`${baseClass}-form-group-item`}
                      label={'Longitude'}
                      floatLabel
                      name={'longitude'}
                      type="text"
                      onChange={handleFieldChange('longitude')}
                      onBlur={handleBlur}
                      value={rawLongitude}
                      errors={
                        errors.longitude && touched.longitude
                          ? errors.longitude
                          : ''
                      }
                    />
                  </div>
                  {submissionError && (
                    <div
                      style={{
                        color: 'red',
                        textAlign: 'center',
                        marginBottom: '1rem',
                      }}
                    >
                      {submissionError}
                    </div>
                  )}
                  <div className={`${baseClass}-form-btns`}>
                    <Button
                      type="submit"
                      isDisabled={isSubmitting || isMapSearch}
                      isLoading={isSubmitting}
                    >
                      Save
                    </Button>
                    <Button
                      type="reset"
                      theme={'secondary'}
                      isDisabled={!dirty}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
      <UIMapDynamic geoData={searchResults} />
    </div>
  );

  return (
    <UICollapsibleSection
      title="Add Property"
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      className={`${baseClass} ${className}`.trim()}
    >
      {renderContent()}
    </UICollapsibleSection>
  );
};

export default AddPropertyForm;
